<i18n>
{
"fr": {
"select": "Choisir",
"outward-journey": "Aller",
"return-journey": "Retour",
"price-from": "a partir de"
},
"en": {
"select": "Select",
"outward-journey": "Outward journey",
"return-journey": "Return journey",
"price-from": "from"
}
}
</i18n>
<template>
  <v-lazy
    :options="{threshold: .5}"
    min-height="150"
    transition="slide-y-transition"
  >
    <v-card
      v-intersect="{
        handler: onIntersect,
        options: {
          threshold: [0.20]
        }
      }"
      flat
      tile
      :disabled="isDisabled"
    >
      <v-row align="start">
        <v-col
          cols="3"
          class="d-flex flex-column"
        >
          <small class="mt-2 text-center">{{ $t('price-from') }}</small>
          <h2 class="mb-2 text-center">
            {{ formatPrice(priceDisplay, groupPrice[0].price.currency) }}
          </h2>
          <div class="mx-auto">
            <v-btn
              class="mb-2"
              color="primary"
              width="130"
              :disabled="!canSubmit"
              :loading="loadingSubmitItinerary"
              depressed
              @click="submitItinerary"
            >
              {{ $t('select') }}
            </v-btn>
          </div>
        </v-col>

        <v-col
          cols="9"
        >
          <div class="ml-2 my-2">
            <h3 class="mb-0">
              <v-icon
                color="accent"
                class="mr-2"
              >
                mdi-airplane mdi-rotate-90
              </v-icon>
              <span
                class="font-weight-bold heading mx-auto"
              >
                {{ $t('outward-journey') }}
              </span>
            </h3>
          </div>

          <v-divider
            class="mr-8"
          />

          <v-row class="outward-flights rounded-0">
            <v-col class="py-0">
              <v-item-group
                v-model="selectedFlightOutward"
                class="elevation-0"
              >
                <v-expansion-panels
                  :value="outwardPanel"
                  flat
                  readonly
                  accordion
                >
                  <v-item
                    v-for="(flight, index) in flightsOutward"
                    v-slot="{active, toggle}"
                    :key="index"
                    :value="flight"
                    class="my-1"
                  >
                    <v-expansion-panel
                      class="border_clear elevation-0 ma-0 pa-0"
                    >
                      <v-expansion-panel-header
                        class="ma-0 pl-0 py-0"
                        hide-actions
                      >
                        <FlightItinerary
                          :flight="flight"
                          :active="active"
                          :can-selected="canSelected(flight)"
                          :index="index"
                          :opened-panel="outwardPanel"
                          @toggle="toggle"
                          @change-open="changePanel($event, 'outward')"
                        />
                      </v-expansion-panel-header>
                      <v-expansion-panel-content
                        eager
                        class="pa-0"
                      >
                        <FlightItineraryDetails
                          :flight="flight"
                          :refundable="isRefundable(flight)"
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-item>
                </v-expansion-panels>
              </v-item-group>
            </v-col>
          </v-row>

          <div
            v-if="travelType === 'two-way'"
            class="ml-2 my-4"
          >
            <h3
              class="mb-0"
            >
              <v-icon
                color="accent"
                class="mr-2"
              >
                mdi-airplane mdi-rotate-270
              </v-icon>
              <span
                class="font-weight-bold heading mx-auto"
              >
                {{ $t('return-journey') }}
              </span>
            </h3>
          </div>

          <v-divider
            class="mr-8"
          />

          <v-row
            v-if="travelType === 'two-way'"
            class="return-flights"
          >
            <v-col class="py-0">
              <v-item-group
                v-model="selectedFlightReturn"
              >
                <v-expansion-panels
                  :value="returnPanel"
                  :flat="true"
                  readonly
                  accordion
                >
                  <v-item
                    v-for="(flight, index) in flightsReturn"
                    v-slot="{active, toggle}"
                    :key="index"
                    :value="flight"
                    class="my-1"
                  >
                    <v-expansion-panel
                      class="border_clear elevation-0 ma-0 pa-0"
                    >
                      <v-expansion-panel-header
                        class="ma-0 pl-0 py-0"
                        hide-actions
                      >
                        <FlightItinerary
                          :flight="flight"
                          :active="active"
                          :can-selected="canSelected(flight)"
                          :index="index"
                          :opened-panel="returnPanel"
                          @toggle="toggle"
                          @change-open="changePanel($event, 'return')"
                        />
                      </v-expansion-panel-header>
                      <v-expansion-panel-content
                        eager
                        class="pa-0"
                      >
                        <FlightItineraryDetails
                          :flight="flight"
                          :refundable="isRefundable(flight)"
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-item>
                </v-expansion-panels>
              </v-item-group>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <FlightSelectPrice
        v-if="dialogFlightSelectPrice"
        :price-details="groupePriceSelectedDisplay"
        :group-price="groupPrice"
        :group-price-revalidate="optionsAvailableDetailRevalidate"
        @close="dialogFlightSelectPrice = false"
      />
    </v-card>
  </v-lazy>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
// import FlightGroupPriceBillingDetails from '../FlightGroupPriceBillingDetails/FlightGroupPriceBillingDetails'
import FlightItinerary from '../FlightItinerary/FlightItinerary'
import FlightItineraryDetails from '../FlightItineraryDetails/FlightItineraryDetails'
import FlightSelectPrice from '@/components/Flight/FlightSearchResults/FlightSearchResultsGroupPrice/FlightSelectPrice/FlightSelectPrice'
import flightService from '@/services/flightService'

export default {
  name: 'FlightSearchResultsGroupPrice',
  components: {
    FlightSelectPrice,
    FlightItineraryDetails,
    FlightItinerary
  },

  props: {
    groupPrice: {
      type: Array,
      required: true
    },
    thePrice: {
      type: Number,
      required: true
    },
    isDisabled: Boolean,
    triggerUnSelect: Boolean
  },

  data: () => ({
    outwardPanel: undefined,
    returnPanel: undefined,
    identifier: String,
    dialogFlightSelectPrice: false,
    selectedFlightOutward: undefined,
    selectedFlightReturn: undefined,
    loadingSubmitItinerary: false,
    optionsAvailableDetailRevalidate: undefined
  }),
  computed: {
    ...mapState({
      travelType: state => state.fb.searchResults.travelType,
      searchParametersTravellers: state => state.fb.passengersInformation.searchParametersTravellers
    }),
    /**
     * This computed take all the flightPair in the groupPrice
     * (supplier always send us flightPair and not an array of outward flight or return flight)
     * and then return only the outward flight filtered by an identifier and ordered by departure time and duration
     * @return {Object[]}
     */
    flightsOutward () {
      const flights = []
      let isTf = false
      if (this.groupPrice.length > 0) {
        for (const flightPair in this.groupPrice) {
          if (this.groupPrice[flightPair].identifier.indexOf('TN_') >= 0) {
            isTf = true
          }
          flights.push(this.groupPrice[flightPair].flight_outward)
        }
      }
      let uniqList
      if (isTf) {
        uniqList = _.uniqBy(flights, (obj) => {
          // return obj.unique_flight_id.substring(obj.unique_flight_id.indexOf('_'))
          return obj.unique_flight_id
        })
      } else {
        uniqList = _.uniqBy(flights, (obj) => {
          return obj.unique_flight_id
        })
      }
      return _.orderBy(uniqList, [(obj) => {
        return moment(obj.begin).format('HH')
      }, 'duration'], ['asc', 'asc'])
    },

    /**
     * This computed take all the flightPair in the groupPrice
     * (supplier always send us flightPair and not an array of outward flight or return flight)
     * and then return only the return flight filtered by an identifier and ordered by departure time and duration
     * @return {Object[]}
     */
    flightsReturn () {
      if (this.travelType === 'one-way') {
        return undefined
      }
      const flights = []
      let isTf = false
      if (this.groupPrice.length > 0) {
        for (const flightPair in this.groupPrice) {
          if (this.groupPrice[flightPair].identifier.indexOf('TN_') >= 0) {
            isTf = true
          }
          flights.push(this.groupPrice[flightPair].flight_return)
        }
      }
      let uniqList
      if (isTf) {
        uniqList = _.uniqBy(flights, (obj) => {
          // return obj.unique_flight_id.substring(obj.unique_flight_id.indexOf('_'))
          return obj.unique_flight_id
        })
      } else {
        uniqList = _.uniqBy(flights, (obj) => {
          return obj.unique_flight_id
        })
      }
      return _.orderBy(uniqList, [(obj) => {
        return moment(obj.begin).format('HH')
      }, 'duration'], ['asc', 'asc'])
    },
    /**
     * Return true if a flight is selected (need for the watcher to emit an event)
     * @return {boolean}
     */
    hasSelection () {
      return !(this.selectedFlightOutward === undefined && this.selectedFlightReturn === undefined)
    },
    /**
     * Used by the v-btn to control its disabled state
     * @return {boolean}
     */
    canSubmit () {
      let canSubmit = false
      if (this.travelType === 'one-way') {
        canSubmit = this.selectedFlightOutward !== undefined
      } else {
        canSubmit = this.selectedFlightOutward !== undefined && this.selectedFlightReturn !== undefined
      }
      return canSubmit
    },
    /**
     * Used for display price when outward and return selected
     * @return {number}
     */
    priceDisplay () {
      if (this.travelType === 'one-way') {
        if (this.selectedFlightOutward !== undefined) {
          const itinerary = _.find(this.groupPrice, item => {
            if (item.flight_outward.unique_flight_id === this.selectedFlightOutward.unique_flight_id) {
              return item
            }
          })
          if (itinerary) {
            return itinerary.price.total
          }
        }
      } else {
        if (this.selectedFlightOutward !== undefined && this.selectedFlightReturn !== undefined) {
          const itinerary = _.find(this.groupPrice, item => {
            if (item.flight_outward.unique_flight_id === this.selectedFlightOutward.unique_flight_id && item.flight_return.unique_flight_id === this.selectedFlightReturn.unique_flight_id) {
              return item
            }
          })
          // console.log('SJISJSI price display ', this.selectedFlightOutward, this.selectedFlightReturn, itinerary)
          if (itinerary) {
            return itinerary.price.total
          }
        }
      }
      return this.thePrice
    },
    /**
     * Used for display price detail when outward and return selected
     * @return {Object}
     */
    groupePriceSelectedDisplay () {
      if (this.travelType === 'one-way') {
        if (this.selectedFlightOutward !== undefined) {
          const itinerary = _.find(this.groupPrice, item => {
            if (item.flight_outward.unique_flight_id === this.selectedFlightOutward.unique_flight_id) {
              return item
            }
          })
          if (itinerary) {
            return itinerary.price
          }
        }
      } else {
        if (this.selectedFlightOutward !== undefined && this.selectedFlightReturn !== undefined) {
          const itinerary = _.find(this.groupPrice, item => {
            if (item.flight_outward.unique_flight_id === this.selectedFlightOutward.unique_flight_id && item.flight_return.unique_flight_id === this.selectedFlightReturn.unique_flight_id) {
              return item
            }
          })
          if (itinerary) {
            return itinerary.price
          }
        }
      }
      return this.groupPrice[0].price
    }
  },

  watch: {
    /**
     * Emit an event based on hasSelection value
     */
    hasSelection () {
      if (this.hasSelection) {
        this.$emit('has-selection', true)
      } else {
        this.$emit('has-selection', false)
      }
    },
    triggerUnSelect () {
      this.selectedFlightOutward = undefined
      this.selectedFlightReturn = undefined
    }
  },

  methods: {
    ...mapActions({
      updateSelectedItinerary: 'fb/flightFinalDetailsPutAction',
      changeError: 'general/changeError'
    }),
    /**
     * Format a raw price based on currency and i18n locale
     * @return {string}
     */
    formatPrice (rawPrice, currency) {
      return new Intl.NumberFormat(
        this.$i18n.locale,
        {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'code',
          maximumFractionDigits: 2
        }).format(rawPrice).replace(/\D00(?=\D*$)/, '')
    },
    /**
     * This method is an event handler,
     * it changes the current opened panel by reacting to an event emmited by FlightItinerary component
     * @return {undefined}
     */
    changePanel (index, direction) {
      if (direction === 'outward') {
        if (index === this.outwardPanel) {
          this.outwardPanel = undefined
        } else {
          this.outwardPanel = index
        }
      } else if (direction === 'return') {
        if (index === this.returnPanel) {
          this.returnPanel = undefined
        } else {
          this.returnPanel = index
        }
      }
    },
    /**
     * Find and itinerary with lodash find based on its identifier and update the selected itinerary in the vueX store
     * @return {undefined}
     */
    submitItinerary () {
      // ici on va chercher les alternatives possibles de l'itinéraire sélectionné
      this.loadingSubmitItinerary = true
      let itinerary = null
      if (this.travelType === 'one-way') {
        if (this.selectedFlightOutward !== undefined) {
          itinerary = _.find(this.groupPrice, item => {
            if (item.flight_outward.unique_flight_id === this.selectedFlightOutward.unique_flight_id) {
              return item
            }
          })
        }
      } else {
        if (this.selectedFlightOutward !== undefined && this.selectedFlightReturn !== undefined) {
          itinerary = _.find(this.groupPrice, item => {
            if (item.flight_outward.unique_flight_id === this.selectedFlightOutward.unique_flight_id && item.flight_return.unique_flight_id === this.selectedFlightReturn.unique_flight_id) {
              return item
            }
          })
        }
      }

      if (itinerary) {
        itinerary = _.cloneDeep(itinerary)
        // console.log('SJISJI submitItinerary itinerary : ', itinerary)
        // console.log('SJISJI submitItinerary itinerary : ', itinerary.price.options_fares)
        itinerary.insurances = 'AISC insurance'
        itinerary.light_request = true
        itinerary.for_revalidate = true
        itinerary.options_fares = itinerary.price.options_fares
        const nbTravellers = []
        const nbTravellersTmp = []
        for (const traveller of this.searchParametersTravellers) {
          if (nbTravellersTmp[traveller.traveller_type]) {
            nbTravellersTmp[traveller.traveller_type] = nbTravellersTmp[traveller.traveller_type] + 1
          } else {
            nbTravellersTmp[traveller.traveller_type] = 1
          }
        }
        for (var key in nbTravellersTmp) {
          nbTravellers.push({ number: nbTravellersTmp[key], traveller_type: key })
        }
        itinerary.nb_travellers = nbTravellers

        // console.log('SJISJI submitItinerary itinerary : ', itinerary)
        flightService.flightDetails(itinerary).then(({ data }) => {
          // console.log('SJISJI submitItinerary flightDetails response : ', data)
          this.optionsAvailableDetailRevalidate = data
          this.dialogFlightSelectPrice = true
          this.loadingSubmitItinerary = false
        }).catch((error) => {
          this.loadingSubmitItinerary = false
          console.log('submitItinerary flightDetails error : ', error)
        })
      } else {
        this.loadingSubmitItinerary = false
      }
    },
    /**
     * Return the cleaned identifier for travelfustion uniq id
     * @return {cleanedIdentifier}
     */
    getIdentifier (identifier, forTF) {
      let cleanedIdentifier = identifier
      if (forTF) {
        cleanedIdentifier = cleanedIdentifier.substring(cleanedIdentifier.indexOf('_'))
      }
      return cleanedIdentifier
    },
    /**
     * Return true if a groupe price flight is refundable
     * @return {boolean}
     */
    isRefundable (flight) {
      const itinerary = _.find(this.groupPrice, item => {
        if (this.getIdentifier(item.flight_outward.unique_flight_id, false) === this.getIdentifier(flight.identifier, false)) {
          return item
        }
      })
      if (typeof (itinerary) !== 'undefined') {
        return itinerary.price.passengers_prices[0].refundable === 'true'
      } else {
        return false
      }
    },
    /**
     * Return true if a flight can be selected
     * @return {boolean}
     */
    canSelected (item) {
      if (this.travelType === 'one-way') {
        return true
      }
      if (this.selectedFlightOutward !== undefined) {
        if (item.unique_flight_id !== this.selectedFlightOutward.unique_flight_id) {
          if (this.selectedFlightReturn !== undefined) {
            if (item.unique_flight_id === this.selectedFlightReturn.unique_flight_id) {
              return true
            }
            return false
          } else {
            // je suis dans un allé selected et pas de retour selected
            // si le retour est dans la liste des compatibles alors return true sinon return false
            const itinerariesReturnPossible = _.filter(this.groupPrice, val => {
              if (val.flight_outward.unique_flight_id === this.selectedFlightOutward.unique_flight_id) {
                return val
              }
            })
            if (_.findIndex(itinerariesReturnPossible, function (o) { return o.flight_return.unique_flight_id === item.unique_flight_id }) >= 0) {
              return true
            } else {
              return false
            }
          }
        } else {
          return true
        }
      }
      if (this.selectedFlightReturn !== undefined) {
        if (item.unique_flight_id !== this.selectedFlightReturn.unique_flight_id) {
          // A REVALIDER
          // je suis dans un retour selected et pas d'allé selected
          // si l'allé est dans la liste des compatibles alors return true sinon return false
          const itinerariesReturnPossible = _.filter(this.groupPrice, val => {
            if (val.flight_return.unique_flight_id === this.selectedFlightReturn.unique_flight_id) {
              return val
            }
          })
          if (_.findIndex(itinerariesReturnPossible, function (o) { return o.flight_outward.unique_flight_id === item.unique_flight_id }) >= 0) {
            return true
          } else {
            return false
          }
        } else {
          return true
        }
      }
      return true
    },
    onIntersect (entries) {
      this.$emit('is-intersecting', entries[0].isIntersecting)
    }
  }
}
</script>

<style scoped>
.border_clear {
  border: none !important;
  box-shadow: none !important;
}

.v-item-group {
  z-index: inherit;
}

.v-expansion-panel {
  border: none;
}

.v-expansion-panel::before {
  box-shadow: none !important;
}

.v-expansion-panel::after {
  border-color: transparent !important;
}

.v-expansion-panel-header:hover {
  cursor: default;
}

.v-expansion-panel >>> .v-expansion-panel-content__wrap {
  padding-left: 0 !important;
}

.v-expansion-panels {
  box-shadow: none !important;
}

</style>
